import React, { useState, useEffect, useRef, useMemo } from 'react'
import axios from 'axios'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useSkipper } from './Table'

const columnHelper = createColumnHelper()

const uppercase = info => {
  const value = info.getValue()
  return value ? value.toUpperCase() : ""
}

const inputStyle = { borderWidth: 0 }

const FieldInput = info => {
  return (
    <input
      className="form-control"
      style={inputStyle}
      value={info.getValue()}
      onChange={e => info.table.options.meta?.updateData(info.row.index, info.column.id, e.target.value)}
    />
  )
}

const componentSelect = facility => info => {
  return (
    <select
      className="form-control"
      style={inputStyle}
      value={info.getValue()}
      onChange={e => info.table.options.meta?.updateData(info.row.index, info.column.id, e.target.value !== 'Select' ? e.target.value : null)}
    >
      <option key={null} value={null}>Select</option>
      {Object.keys(facility.components).map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
    </select>
  )
}

const locationSubmit = facility => info => {
  const name = info.getValue()
  const submit = async () => {
    const data = {
      component: info.row.getValue('component'),
      position: info.row.getValue('position'),
      cluster: info.row.getValue('cluster')
    }
    await axios.put(`/api/v1/channel/${name}/move?organisation=${facility.organisation}&facility=${facility.id}`, data)
  }
  return (
    <button className="btn btn-outline-primary" type="button" onClick={submit}>Move</button>
  )
}

const deleteSubmit = facility => info => {
  const name = info.getValue()
  const submit = async () => {
    await axios.delete(`/api/v1/channel/${name}?organisation=${facility.organisation}&facility=${facility.id}`)
  }
  return (
    <button className="btn btn-outline-danger" type="button" onClick={submit}>Delete</button>
  )
}

function ChannelTable({facility}) {
  const [data, setData] = useState([])
  const nrChannels = Object.values(facility.channels).length
  useEffect(() => {
    setData(Object.values(facility.channels))
  }, [facility.id, nrChannels])

  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: () => 'name',
      cell: uppercase,
      size: 5,
    }),
    columnHelper.accessor('component', {
      header: () => 'component',
      cell: componentSelect(facility),
      size: 10,
    }),
    columnHelper.accessor('position', {
      header: () => 'position',
      cell: FieldInput,
      size: 10,
    }),
    columnHelper.accessor('cluster', {
      header: () => 'cluster',
      cell: FieldInput,
      size: 10,
    }),
    columnHelper.accessor('name', {
      id: 'move',
      header: null,
      cell: locationSubmit(facility),
      size: 5,
    }),
    columnHelper.accessor('certificate', {
      header: () => 'certificate',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('serial_nr', {
      header: () => 'serial nr',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('sensor_id', {
      header: () => 'sensor id',
      cell: info => info.getValue(),
    }),
    columnHelper.accessor('name', {
      id: 'delete',
      header: null,
      cell: deleteSubmit(facility),
      size: 5,
    }),
  ], [facility.id])

  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper()

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      updateData: (rowIndex, columnId, value) => {
        skipAutoResetPageIndex()
        setData(data.map((row, index) => {
            if (index === rowIndex) {
              return {
                ...data[rowIndex],
                [columnId]: value,
              }
            }
            return row
          })
        )
      },
    },
  })

  return (
    <table className="table table-hover table-flush align-items-center">
      <thead>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <th key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map(row => (
          <tr key={row.id}>
            {row.getVisibleCells().map(cell => (
              <td key={cell.id} style={{padding: 1, width: cell.column.getSize()}}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const ChannelForm = ({ facility }) => {
  const components = Object.keys(facility.components)
  const name = useRef('')
  const component = useRef(null)
  const cluster = useRef('')
  const position = useRef('')
  const serial = useRef('')
  const certificate = useRef('')
  const type = useRef('TEM')
  const direction = useRef('IN')
  const ID = useRef(0)
  const [show, setShow] = useState(false)
  const [error, setError] = useState(null)

  const submit = async e => {
    const data = {
      name: name.current?.value||"",
      component: component.current?.value||null,
      facility: facility.id,
      organisation: facility.organisation,
      position: position.current?.value||"",
      cluster: cluster.current?.value||"",
      serial_nr: serial.current?.value||"",
      certificate: certificate.current?.value||"",
      type: type.current?.value||"TEM",
      direction: direction.current?.value||"IN",
      sensor_id: ID.current?.value||null
    }
    try {
      await axios.post(`/api/v1/channel/?organisation=${facility.organisation}`, data)
      setShow(false)
      setError(null)
    }
    catch (error) {
      console.log(`Failed to add channel to ${facility.name}`, error)
      setError(error.response?.data||"Error from server")
    }
  }

  const close = () => {
    setShow(false)
    setError(null)
  }

  return (
    <>
    {show && <><div className="modal fade show">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">Add channel to {facility.name.toUpperCase()}</div>
          <div className="modal-body">
            <div>
              {error && <div class="alert alert-danger">{error}</div>}
              <div className="form-group mb-1">
                <label className="form-control-label">Name</label>
                <input className="form-control" placeholder="Sensor1" ref={name}/>
              </div>
              <div className="form-group mb-1">
                <label className="form-control-label">Location</label>
                <select className="form-control" ref={component}>
                  <option value="Component">Component</option>
                  {components.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                </select>
                <input className="form-control" placeholder="Cluster" ref={cluster}/>
                <input className="form-control" placeholder="Position" ref={position}/>
              </div>
              <div className="form-group mb-1">
                <label className="form-control-label">Type</label>
                <select className="form-control" ref={type}>
                  <option value={'TEM'}>Temperature</option>
                  <option value={'HUM'}>Humidity</option>
                  <option value={'FAN'}>Fan</option>
                  <option value={'PID'}>PID</option>
                  <option value={'IO'}>Input/Output</option>
                </select>
              </div>
              <div className="form-group mb-1">
                <label className="form-control-label">Direction</label>
                <select className="form-control" ref={direction}>
                  <option value={'IN'}>In</option>
                  <option value={'OUT'}>Out</option>
                </select>
              </div>
              <div className="form-group mb-1">
                <label className="form-control-label">Serial number</label>
                <input className="form-control" placeholder="000.000.000" ref={serial}/>
              </div>
              <div className="form-group mb-1">
                <label className="form-control-label">Certificate</label>
                <input className="form-control" placeholder="20XXXXXXXXXXX" ref={certificate}/>
              </div>
              <div className="form-group mb-1">
                <label className="form-control-label">Sensor ID</label>
                <input className="form-control" type="number" placeholder="Sensor id" ref={ID}/>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <div className="btn" onClick={close}>Cancel</div>
            <div className="btn btn-primary" onClick={submit}>OK</div>
          </div>
        </div>
      </div>
    </div>
    <div className="modal-backdrop fade show" onClick={close}/></>}
    <button className="btn btn-primary" onClick={e => setShow(true)}>New</button>
    </>
  )
}

export default function Channels({ facility }) {
  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col card">
          <div className="card-header bg-transparent">
            <div className="row align-items-center">
              <div className="col">
                <h5 className="card-title text-uppercase text-muted mb-0">{facility?.name}</h5>
                <span className="h2 font-weight-bold mb-0">Sensors</span>
              </div>
              <div className="col-auto">
                <ChannelForm facility={facility}/>
              </div>
            </div>
          </div>
          <ChannelTable facility={facility}/>
        </div>
      </div>
    </div>
  )
}
